import { FC, useMemo, useState } from 'react';
import {
  makeStyles,
  Field,
  Tag,
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  useTagPickerFilter,
  Caption1Strong,
  tokens,
} from '@fluentui/react-components';

import { TFolder, TSparseItem } from '@modules/items';
import { AccessType } from '@modules/access';
import { ARCHIVED_JURISDICTION_VIRTUAL_ID } from '@modules/submissions/constants';

const NO_MATCHES_FILTER_OPTION_VALUE = 'no-matches';

type TProps = {
  label: string;
  folder: TFolder;
  withBlocks?: boolean;
  withArchived?: boolean;
  selectedFolderIds: string[];
  onChangeSelectedFolderIds: (folderIds: string[]) => void;
};

export const JurisdictionFilter: FC<TProps> = ({
  label,
  folder,
  withBlocks = false,
  withArchived = false,
  selectedFolderIds,
  onChangeSelectedFolderIds,
}) => {
  const [query, setQuery] = useState('');

  // Collect all potential jurisdictions (folder children).
  const allFolders = useMemo(() => {
    const res = folder.children.reduce((acc, c) => {
      if (c.folderType === 'BLOCK') {
        if (withBlocks) acc.push({ _id: c._id, name: `${c.name} (shared)` });
      } else if (c.folderType === 'JURISDICTION') {
        acc.push({ _id: c._id, name: c.name });
      }
      return acc;
    }, [] as TSparseItem[]);

    if (withArchived) res.push({ _id: ARCHIVED_JURISDICTION_VIRTUAL_ID, name: 'Archived' });

    return res;
  }, [folder, withBlocks, withArchived]);

  // Create a quick lookup map so we can fetch folder names by ID
  const nameMap = useMemo(() => {
    const map: Record<string, string> = {};
    allFolders.forEach((f) => {
      map[f._id] = f.name;
    });
    return map;
  }, [allFolders]);

  // Prepare TagPicker’s filter logic
  const children = useTagPickerFilter({
    query,
    options: allFolders.map((f) => f._id),
    noOptionsElement: (
      <TagPickerOption value={NO_MATCHES_FILTER_OPTION_VALUE}>No matching jurisdiction.</TagPickerOption>
    ),
    renderOption: (folderId) => (
      <TagPickerOption
        value={folderId}
        key={folderId}
        text={nameMap[folderId]}
        className={folderId === ARCHIVED_JURISDICTION_VIRTUAL_ID ? '!text-red-500' : ''}
      >
        {nameMap[folderId]}
      </TagPickerOption>
    ),
    filter: (folderId) => {
      // Exclude already-selected IDs
      if (selectedFolderIds.includes(folderId)) return false;
      // Check if the name matches the query
      return nameMap[folderId]?.toLowerCase().includes(query.toLowerCase());
    },
  });

  // When an option is selected or unselected
  const onOptionSelect: TagPickerProps['onOptionSelect'] = (_, data) => {
    if (data.value === NO_MATCHES_FILTER_OPTION_VALUE) {
      return;
    }
    onChangeSelectedFolderIds(data.selectedOptions as string[]);
    setQuery(''); // reset the input
  };

  // Check if any selected jurisdictions have missing read permissions
  const missingReadPermissions: string[] = useMemo(() => {
    return selectedFolderIds
      .filter((folderId) => {
        const foundChild = folder.children.find((c) => c._id === folderId);
        // If we can't find the child for some reason, treat as missing, or skip
        if (!foundChild) return false;
        return foundChild.accessRight < AccessType.READ;
      })
      .map((folderId) => nameMap[folderId] || 'Unnamed');
  }, [selectedFolderIds]);

  return (
    <div className="w-full">
      <Field label={label} className="w-full">
        <TagPicker onOptionSelect={onOptionSelect} selectedOptions={selectedFolderIds} size="medium">
          <TagPickerControl>
            <TagPickerGroup aria-label="Selected jurisdictions">
              {selectedFolderIds.map((folderId) => (
                <Tag key={folderId} shape="rounded" value={folderId}>
                  <span className={folderId === ARCHIVED_JURISDICTION_VIRTUAL_ID ? '!text-red-500' : ''}>
                    {nameMap[folderId] || 'Unnamed'}
                  </span>
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label="Select jurisdictions"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </TagPickerControl>
          <TagPickerList>{children}</TagPickerList>
        </TagPicker>
      </Field>

      {missingReadPermissions.length > 0 && (
        <Caption1Strong className="mt-1 text-red-500">
          Missing read permissions for jurisdictions: {missingReadPermissions.join(', ')}.
        </Caption1Strong>
      )}
    </div>
  );
};
